import React from "react"
import { Helmet } from "react-helmet"

import Layout from "../Layout"
import IndustryHero from "./sections/IndustryHero"
import ChallengeGrid from "./sections/ChallengeGrid"
import Solution from "./sections/Solution"
import SituationsSection from "./sections/SituationsSection"
import Results from "./sections/Results"
import TrustedBy from "./sections/TrustedBy"
import WhyVisiomate from "./sections/WhyVisiomate"
import Questions from "./sections/Questions"
import CtaBanner from "./sections/CtaBanner"

import "../../styles/industryPage.scss"

const SITE = "https://www.visiomate.com"

/**
 * INDUSTRY PAGE TEMPLATE — rebuilt to match src/Design/industries/
 * industry-*.html exactly: the same eight sections, in the same order, on
 * all seven industry pages (real estate included — it now uses this
 * template rather than its own bespoke component set).
 *
 * Section order: IndustryHero (with stats) → ChallengeGrid → Solution (or
 * SituationsSection) → Results → TrustedBy → WhyVisiomate → Questions →
 * CtaBanner. Six of the seven pages now provide a `situations` array and
 * render SituationsSection ("Wherever you're starting from") in that slot
 * instead of Solution; real estate has no `situations` field, so it keeps
 * rendering Solution exactly as before — this is the ONLY thing that
 * branches per page in this template, everything else is still shared.
 *
 * Title tag, meta description and canonical are UNCHANGED from before this
 * rebuild: metaTitle/metaDescription are passed straight through, and
 * canonical is still generated automatically by Layout.js from the real
 * URL. FAQPage and BreadcrumbList JSON-LD are built here from the same
 * `faqs` array Questions renders, so the schema can never disagree with
 * what's visibly on the page.
 */
const IndustryPageTemplate = ({ data }) => {
  const {
    slug,
    industryName,
    kicker,
    headline,
    subheading,
    heroImage,
    heroImageAlt,
    ctaHref,
    ctaLabel,
    badge,
    stats,

    challengesEyebrow,
    challengesHeading,
    challengesIntro,
    challenges,

    solutionEyebrow,
    solutionHeading,
    solutionOfferName,
    solutionFit,
    solutionTimelineValue,
    solutionTimelineLabel,
    solutionFeatures,
    solutionLinkLabel,
    solutionLinkHref,
    solutionImage,
    solutionImageAlt,

    situationsEyebrow,
    situationsHeading,
    situationsIntro,
    situations,
    situationsUnsureHeading,
    situationsUnsureDetail,
    situationsUnsureLinkLabel,
    situationsUnsureLinkHref,

    resultsEyebrow,
    resultsHeading,
    resultsMetrics,
    resultsSummary,
    resultsLinkLabel,
    resultsLinkHref,
    resultsLinks,
    resultsImage,
    resultsImageAlt,

    trustedByLogos,

    whyEyebrow,
    whyHeading,
    whyIntro,
    whyLinkHref,
    reasons,

    questionsEyebrow,
    questionsHeading,
    questionsIntro,
    questionsLinkHref,
    faqs,

    ctaHeading,
    ctaDetail,
    ctaLinkLabel,
    ctaLinkHref,

    metaTitle,
    metaDescription,
  } = data

  const url = slug ? `${SITE}/industries/${slug}` : undefined

  const faqSchema =
    faqs && faqs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map(f => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }
      : null

  const breadcrumbSchema = url
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE },
          { "@type": "ListItem", position: 2, name: industryName, item: url },
        ],
      }
    : null

  return (
    <Layout pageTitle={metaTitle} descrip={metaDescription}>
      <Helmet>
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
        {breadcrumbSchema && (
          <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        )}
      </Helmet>

      <article className="ip">
        <IndustryHero
          industryName={industryName}
          kicker={kicker}
          headline={headline}
          subheading={subheading}
          heroImage={heroImage}
          heroImageAlt={heroImageAlt}
          ctaHref={ctaHref}
          ctaLabel={ctaLabel}
          badge={badge}
          stats={stats}
        />

        <ChallengeGrid
          eyebrow={challengesEyebrow}
          heading={challengesHeading}
          intro={challengesIntro}
          items={challenges}
        />

        {situations ? (
          <SituationsSection
            eyebrow={situationsEyebrow}
            heading={situationsHeading}
            intro={situationsIntro}
            cards={situations}
            unsureHeading={situationsUnsureHeading}
            unsureDetail={situationsUnsureDetail}
            unsureLinkLabel={situationsUnsureLinkLabel}
            unsureLinkHref={situationsUnsureLinkHref}
          />
        ) : (
          <Solution
            eyebrow={solutionEyebrow}
            heading={solutionHeading}
            offerName={solutionOfferName}
            fit={solutionFit}
            timelineValue={solutionTimelineValue}
            timelineLabel={solutionTimelineLabel}
            features={solutionFeatures}
            linkLabel={solutionLinkLabel}
            linkHref={solutionLinkHref}
            image={solutionImage}
            imageAlt={solutionImageAlt}
          />
        )}

        <Results
          eyebrow={resultsEyebrow}
          heading={resultsHeading}
          metrics={resultsMetrics}
          summary={resultsSummary}
          linkLabel={resultsLinkLabel}
          linkHref={resultsLinkHref}
          links={resultsLinks}
          image={resultsImage}
          imageAlt={resultsImageAlt}
        />

        <TrustedBy names={trustedByLogos} />

        <WhyVisiomate eyebrow={whyEyebrow} heading={whyHeading} intro={whyIntro} linkHref={whyLinkHref} reasons={reasons} />

        <Questions
          eyebrow={questionsEyebrow}
          heading={questionsHeading}
          intro={questionsIntro}
          linkHref={questionsLinkHref}
          faqs={faqs}
        />

        <CtaBanner
          heading={ctaHeading}
          detail={ctaDetail}
          ctaHref={ctaLinkHref}
          ctaLabel={ctaLinkLabel}
          backgroundImage={heroImage}
        />
      </article>
    </Layout>
  )
}

export default IndustryPageTemplate
